import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

import { redeem, redeemPendingCode, referralState, clearReferralState } from '../services/referral';

const AuthContext = createContext(null);

// HIPAA §164.312(a)(2)(iii): Automatic logoff after 15 min inactivity
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;
const WARNING_BEFORE_MS = 60 * 1000; // show warning 1 min before logout

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [referral, setReferral] = useState(referralState);
  const inactivityTimer = useRef(null);
  const warningTimer = useRef(null);

  const clearTimers = () => {
    clearTimeout(inactivityTimer.current);
    clearTimeout(warningTimer.current);
  };

  const startTimers = useCallback(() => {
    clearTimers();
    setSessionWarning(false);
    warningTimer.current = setTimeout(
      () => setSessionWarning(true),
      INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_MS
    );
    inactivityTimer.current = setTimeout(() => {
      // HIPAA: auto-logout after inactivity
      sessionStorage.removeItem('hcai_token');
      setUser(null);
      setSessionWarning(false);
      window.location.href = '/login?reason=timeout';
    }, INACTIVITY_TIMEOUT_MS);
  }, []);

  const extendSession = useCallback(() => {
    startTimers();
  }, [startTimers]);

  // Activity event listeners — reset timer on any user interaction
  useEffect(() => {
    if (!user) { clearTimers(); return; }
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, startTimers, { passive: true }));
    startTimers();
    return () => {
      events.forEach(e => window.removeEventListener(e, startTimers));
      clearTimers();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, startTimers]);


  // A clinic code is redeemed the first time we hold a token, whether that came
  // from a fresh sign-up, a returning sign-in, or a session already in progress.
  // Deliberately not awaited: attribution is a marketing concern and must never
  // delay or fail a sign-in.
  const claimReferral = useCallback(() => {
    redeemPendingCode(api).then((result) => {
      if (result) setReferral(result);
    });
  }, []);

  /**
   * Redeem a code the user typed, or answer a verification challenge.
   * Awaited, unlike the QR path — someone who just pressed a button is
   * entitled to see what happened.
   */
  const redeemReferralCode = useCallback(async (code, verification) => {
    const result = await redeem(api, code, verification);
    setReferral(result);
    return result;
  }, []);

  const loadUser = useCallback(async () => {
    const token = sessionStorage.getItem('hcai_token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await api.get('/users/me');
      setUser(res.data);
      claimReferral();
    } catch {
      sessionStorage.removeItem('hcai_token');
    } finally {
      setLoading(false);
    }
  }, [claimReferral]);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    sessionStorage.setItem('hcai_token', res.data.token);
    setUser(res.data.user);
    claimReferral();
    return res.data.user;
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    sessionStorage.setItem('hcai_token', res.data.token);
    setUser(res.data.user);
    claimReferral();
    return res.data.user;
  };

  const logout = useCallback(() => {
    sessionStorage.removeItem('hcai_token');
    setUser(null);
    setSessionWarning(false);
    clearTimers();
  }, []);

  const updateUser = (updates) => setUser(u => ({ ...u, ...updates }));

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, updateUser,
      reloadUser: loadUser, sessionWarning, extendSession,
      referral, redeemReferralCode,
      dismissReferral: () => { clearReferralState(); setReferral(null); },
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
